import React, { memo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Input } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { Svg } from '@/shared/ui/Svg';
import { AppButton } from '@/shared/ui/AppButton';
import { useAppDispatch } from '@/shared/lib/hooks/useAppDispatch';
import { useToast } from '@/shared/lib/hooks/useToast';
import { routerPath } from '@/shared/config/router';
import LogoIcon from '@/shared/assets/icons/complex/logo/logo-black.complex.svg';
import SlashedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import { authByEmail } from '../../model/services/authByEmail';
import { authDataSchema } from '../../config/validationSchemas';
import { IAuthFormData } from '../../model/types/AuthFormData';
import styles from './AuthForm.module.scss';

interface AuthFormProps {
  className?: string;
  formId: string;
}

export const AuthForm = memo(({ className, formId }: AuthFormProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IAuthFormData>({
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    } as Partial<IAuthFormData>,
    resolver: yupResolver(authDataSchema),
  });

  const onSubmit = async (data: IAuthFormData) => {
    try {
      const result = await dispatch(authByEmail(data));

      if (authByEmail.fulfilled.match(result)) {
        navigate(routerPath.main);
      } else if (authByEmail.rejected.match(result)) {
        const errorPayload = result.payload;

        if (errorPayload?.message) {
          toast({
            title: 'Authorisation error',
            description: errorPayload.message,
            color: 'danger',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred during authorisation',
        color: 'danger',
      });
    }
  };

  return (
    <div className={cn(styles.wrapper, className)}>
      <Svg className={styles.logo} Icon={LogoIcon} />
      <h1 className={styles.title}>Log In</h1>
      <form className={styles.form} id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.inputContainer}>
          <Input
            isRequired
            type="email"
            label="Email"
            labelPlacement="outside"
            placeholder="Enter your email"
            errorMessage={errors.email?.message}
            variant="bordered"
            radius="sm"
            classNames={{
              inputWrapper: cn(
                styles.inputWrapper,
                errors.email
                  ? 'border-red-500 focus-within:border-red-500'
                  : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
              ),
              label: 'pr-0 text-sm leading-[1.4] font-normal text-black-secondary',
            }}
            {...register('email')}
          />
          <Input
            isRequired
            type={isVisible ? 'text' : 'password'}
            label="Password"
            labelPlacement="outside"
            placeholder="Enter your password"
            errorMessage={errors.password?.message}
            variant="bordered"
            radius="sm"
            classNames={{
              inputWrapper: cn(
                styles.inputWrapper,
                errors.password
                  ? 'border-red-500 focus-within:border-red-500'
                  : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
              ),
              label: 'pr-0 text-sm leading-[1.4] font-normal text-black-secondary',
            }}
            endContent={
              <button
                aria-label="toggle password visibility"
                className="focus:outline-none"
                type="button"
                onClick={() => setIsVisible(!isVisible)}
              >
                {isVisible ? (
                  <Svg
                    Icon={SlashedEyeIcon}
                    className="text-2xl text-default-400 pointer-events-none"
                    width={24}
                    height={24}
                  />
                ) : (
                  <Svg
                    Icon={OpenedEyeIcon}
                    className="text-2xl text-default-400 pointer-events-none"
                    width={24}
                    height={24}
                  />
                )}
              </button>
            }
            {...register('password')}
          />
          <Checkbox
            color="default"
            classNames={{
              base: styles.remember,
              wrapper: styles.rememberBox,
              icon: styles.rememberIcon,
              label: '!text-black-pure group-data-[selected=true]:!text-black-pure',
            }}
            {...register('remember')}
          >
            Remember me
          </Checkbox>
        </div>
        <div className={styles.forgotPassword}>
          <button
            type="button"
            className={styles.forgotLink}
            onClick={() => navigate(routerPath.forgotPassword)}
          >
            Forgot Password?
          </button>
        </div>
        <AppButton type="submit" className={styles.button} color="grey" disabled={isSubmitting}>
          {isSubmitting ? 'Loading...' : 'Log In'}
        </AppButton>
      </form>
    </div>
  );
});
